import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AspectRatio as AspectRatioIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { addRatioToPrompt, removeRatioFromPrompt, updateToolRatio } from '../graphql/mutation';

const commonRatios = [
  { name: '1:1', width: 1024, height: 1024, aspectRatioX: 1, aspectRatioY: 1 },
  { name: '9:16', width: 1080, height: 1920, aspectRatioX: 9, aspectRatioY: 16 },
  { name: '16:9', width: 1920, height: 1080, aspectRatioX: 16, aspectRatioY: 9 },
  { name: '21:9', width: 2560, height: 1080, aspectRatioX: 21, aspectRatioY: 9 },
  { name: '2:3', width: 1024, height: 1536, aspectRatioX: 2, aspectRatioY: 3 },
  { name: '3:2', width: 1536, height: 1024, aspectRatioX: 3, aspectRatioY: 2 },
  { name: '4:5', width: 1024, height: 1280, aspectRatioX: 4, aspectRatioY: 5 },
  { name: '5:4', width: 1280, height: 1024, aspectRatioX: 5, aspectRatioY: 4 },
];

export default function ToolRatioManager({ promptId, toolRatios = [], onRatiosChange, readOnly = false, createMode = false, onLocalRatiosChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRatio, setEditingRatio] = useState(null);
  const [ratioForm, setRatioForm] = useState({
    name: '',
    width: '',
    height: '',
    aspectRatioX: '',
    aspectRatioY: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [addRatioMutation] = useMutation(addRatioToPrompt);
  const [removeRatioMutation] = useMutation(removeRatioFromPrompt);
  const [updateRatioMutation] = useMutation(updateToolRatio);

  const resetForm = () => {
    setRatioForm({
      name: '',
      width: '',
      height: '',
      aspectRatioX: '',
      aspectRatioY: '',
    });
    setEditingRatio(null);
  };

  const handleOpenDialog = (ratio = null) => {
    if (ratio) {
      setEditingRatio(ratio);
      setRatioForm({
        name: ratio.name,
        width: ratio.width.toString(),
        height: ratio.height.toString(),
        aspectRatioX: ratio.aspectRatioX.toString(),
        aspectRatioY: ratio.aspectRatioY.toString(),
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleFormChange = (field, value) => {
    setRatioForm(prev => ({ ...prev, [field]: value }));
  };

  const handleQuickAdd = async (commonRatio) => {
    try {
      // Check if this ratio already exists
      const existingRatio = toolRatios.find(
        ratio => ratio.aspectRatioX === commonRatio.aspectRatioX && 
                 ratio.aspectRatioY === commonRatio.aspectRatioY
      );

      if (existingRatio) {
        // Ratio exists - remove it (toggle off)
        await handleDeleteRatio(existingRatio.id, true); // true = skip confirmation
      } else {
        // Ratio doesn't exist - add it (toggle on)
        const ratioData = {
          name: commonRatio.name,
          width: commonRatio.width,
          height: commonRatio.height,
          aspectRatioX: commonRatio.aspectRatioX,
          aspectRatioY: commonRatio.aspectRatioY,
        };

        if (createMode) {
          // In create mode, manage local ratios
          const newRatio = {
            ...ratioData,
            id: `temp-${Date.now()}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const updatedRatios = [...toolRatios, newRatio];
          if (onLocalRatiosChange) {
            onLocalRatiosChange(updatedRatios);
          }
          setSnackbar({ open: true, message: 'Ratio added successfully!', severity: 'success' });
        } else {
          // Normal mode - use GraphQL mutation
          await addRatioMutation({
            variables: {
              promptId,
              ...ratioData,
            },
            refetchQueries: ['GetPrompt'],
            awaitRefetchQueries: true,
          });
          setSnackbar({ open: true, message: 'Ratio added successfully!', severity: 'success' });

          if (onRatiosChange) {
            setIsRefreshing(true);
            await onRatiosChange();
            setIsRefreshing(false);
          }
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' });
    }
  };

  const handleSaveRatio = async () => {
    try {
      const ratioData = {
        name: ratioForm.name,
        width: parseInt(ratioForm.width),
        height: parseInt(ratioForm.height),
        aspectRatioX: parseInt(ratioForm.aspectRatioX),
        aspectRatioY: parseInt(ratioForm.aspectRatioY),
      };

      if (createMode) {
        // In create mode, manage local ratios
        const newRatio = {
          ...ratioData,
          id: editingRatio ? editingRatio.id : `temp-${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        if (editingRatio) {
          // Update existing local ratio
          const updatedRatios = toolRatios.map(ratio => 
            ratio.id === editingRatio.id ? newRatio : ratio
          );
          if (onLocalRatiosChange) {
            onLocalRatiosChange(updatedRatios);
          }
          setSnackbar({ open: true, message: 'Ratio updated successfully!', severity: 'success' });
        } else {
          // Add new local ratio
          const updatedRatios = [...toolRatios, newRatio];
          if (onLocalRatiosChange) {
            onLocalRatiosChange(updatedRatios);
          }
          setSnackbar({ open: true, message: 'Ratio added successfully!', severity: 'success' });
        }
      } else {
        // Normal mode - use GraphQL mutations
        if (editingRatio) {
          // Update existing ratio
          await updateRatioMutation({
            variables: {
              id: editingRatio.id,
              ...ratioData,
            },
            refetchQueries: ['GetPrompt'], // Automatically refetch the prompt data
            awaitRefetchQueries: true, // Wait for refetch to complete
          });
          setSnackbar({ open: true, message: 'Ratio updated successfully!', severity: 'success' });
        } else {
          // Add new ratio
          await addRatioMutation({
            variables: {
              promptId,
              ...ratioData,
            },
            refetchQueries: ['GetPrompt'], // Automatically refetch the prompt data
            awaitRefetchQueries: true, // Wait for refetch to complete
          });
          setSnackbar({ open: true, message: 'Ratio added successfully!', severity: 'success' });
        }

        if (onRatiosChange) {
          setIsRefreshing(true);
          await onRatiosChange();
          setIsRefreshing(false);
        }
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving ratio:', error);
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeleteRatio = async (ratioId, skipConfirmation = false) => {
    if (!skipConfirmation && !window.confirm('Are you sure you want to delete this ratio?')) {
      return;
    }

    try {
      if (createMode) {
        // In create mode, manage local ratios
        const updatedRatios = toolRatios.filter(ratio => ratio.id !== ratioId);
        if (onLocalRatiosChange) {
          onLocalRatiosChange(updatedRatios);
        }
        setSnackbar({ open: true, message: 'Ratio deleted successfully!', severity: 'success' });
      } else {
        // Normal mode - use GraphQL mutation
        await removeRatioMutation({
          variables: {
            promptId,
            ratioId,
          },
          refetchQueries: ['GetPrompt'], // Automatically refetch the prompt data
          awaitRefetchQueries: true, // Wait for refetch to complete
        });
        setSnackbar({ open: true, message: 'Ratio deleted successfully!', severity: 'success' });
        if (onRatiosChange) {
          setIsRefreshing(true);
          await onRatiosChange();
          setIsRefreshing(false);
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Helper function to check if a common ratio is already selected
  const isRatioSelected = (commonRatio) => {
    return toolRatios.some(
      ratio => ratio.aspectRatioX === commonRatio.aspectRatioX && 
               ratio.aspectRatioY === commonRatio.aspectRatioY
    );
  };

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tool Ratios Accordion */}
      <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 4,
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AspectRatioIcon />
            <Typography variant='h6' sx={{ fontWeight: 600, color: '#495057' }}>
              Tool Ratios ({toolRatios.length})
            </Typography>
            {isRefreshing && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 4, pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Add Ratio Button */}
            {!readOnly && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Manage aspect ratios for this prompt
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{ borderRadius: 2 }}
                >
                  Add Ratio
                </Button>
              </Box>
            )}

            {/* Quick Add Common Ratios - Always visible when not readOnly */}
            {!readOnly && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#495057' }}>
                  Quick Add Common Ratios:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {commonRatios.map((ratio, index) => {
                    const selected = isRatioSelected(ratio);
                    return (
                      <Chip
                        key={index}
                        label={ratio.name}
                        onClick={() => handleQuickAdd(ratio)}
                        sx={{
                          backgroundColor: selected ? '#1565c0' : '#e3f2fd',
                          color: selected ? '#ffffff' : '#1565c0',
                          '&:hover': { 
                            backgroundColor: selected ? '#0d47a1' : '#bbdefb',
                          },
                          cursor: 'pointer',
                          fontWeight: selected ? 600 : 400,
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Existing Ratios */}
            {toolRatios.length > 0 ? (
              <Grid container spacing={2}>
                {toolRatios.map((ratio) => (
                  <Grid item xs={12} sm={6} md={4} key={ratio.id}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #e9ecef' }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {ratio.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {ratio.width} × {ratio.height}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ratio: {ratio.aspectRatioX}:{ratio.aspectRatioY}
                        </Typography>
                      </CardContent>
                      {!readOnly && (
                        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(ratio)}
                            sx={{ color: '#3498db' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRatio(ratio.id)}
                            sx={{ color: '#e74c3c' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AspectRatioIcon sx={{ fontSize: 48, color: '#bdc3c7', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No ratios defined for this prompt
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {readOnly ? 'Ratios will appear here when added' : 'Click "Add Ratio" to get started'}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Add/Edit Ratio Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRatio ? 'Edit Tool Ratio' : 'Add Tool Ratio'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Ratio Name"
              value={ratioForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="e.g., Square 1:1, Portrait 9:16"
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Width"
                type="number"
                value={ratioForm.width}
                onChange={(e) => handleFormChange('width', e.target.value)}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Height"
                type="number"
                value={ratioForm.height}
                onChange={(e) => handleFormChange('height', e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Aspect Ratio X"
                type="number"
                value={ratioForm.aspectRatioX}
                onChange={(e) => handleFormChange('aspectRatioX', e.target.value)}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Aspect Ratio Y"
                type="number"
                value={ratioForm.aspectRatioY}
                onChange={(e) => handleFormChange('aspectRatioY', e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Box>

            {/* Preview */}
            {ratioForm.aspectRatioX && ratioForm.aspectRatioY && (
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Preview: {ratioForm.aspectRatioX}:{ratioForm.aspectRatioY} ratio
                </Typography>
                <Box
                  sx={{
                    width: Math.min(200, (parseInt(ratioForm.aspectRatioX) / parseInt(ratioForm.aspectRatioY)) * 100),
                    height: Math.min(100, (parseInt(ratioForm.aspectRatioY) / parseInt(ratioForm.aspectRatioX)) * 100),
                    backgroundColor: '#3498db',
                    margin: '8px auto',
                    borderRadius: 1,
                    minWidth: 40,
                    minHeight: 40,
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveRatio}
            variant="contained"
            disabled={!ratioForm.name || !ratioForm.width || !ratioForm.height || !ratioForm.aspectRatioX || !ratioForm.aspectRatioY}
          >
            {editingRatio ? 'Update' : 'Add'} Ratio
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
